/**
 * #995: the garages 422 `PRICE_BELOW_OFFER_DISCOUNT` — a service price a running (or pending) offer
 * covering the service could not discount. `details` names the offer and the lowest price it still
 * discounts (FIXED: amount + 1). The admin portal is English-only.
 */
export const PRICE_GUARD_CODE = 'PRICE_BELOW_OFFER_DISCOUNT';

export interface PriceGuard {
  offerId: string;
  offerTitle: string;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: number;
  minimumPrice: number;
  price: number;
  /** `price` (variant / V1 price) or `flatPrice` (single price). */
  field: string;
}

/** The guard details of an error (admin ApiError: `code` + `details`), or null for any other error. */
export function priceGuardOf(err: unknown): PriceGuard | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { code?: string; details?: Record<string, unknown> };
  if (e.code !== PRICE_GUARD_CODE || !e.details) return null;
  const d = e.details;
  const minimumPrice = Number(d.minimumPrice);
  if (!Number.isFinite(minimumPrice)) return null;
  return {
    offerId: String(d.offerId ?? ''),
    offerTitle: String(d.offerTitle ?? ''),
    discountType: d.discountType === 'PERCENT' ? 'PERCENT' : 'FIXED',
    discountValue: Number(d.discountValue),
    minimumPrice,
    price: Number(d.price),
    field: typeof d.field === 'string' ? d.field : 'price',
  };
}

const amount = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Same wording as the server's message, rebuilt from `details`. */
export function priceGuardMessage(guard: PriceGuard): string {
  const off = guard.discountType === 'PERCENT' ? `${amount(guard.discountValue)}% off` : `AED ${amount(guard.discountValue)} off`;
  return `This price is covered by the offer '${guard.offerTitle}' (${off}). `
    + `Minimum price while the offer runs: AED ${amount(guard.minimumPrice)}. End or edit the offer first.`;
}

/** True when a typed price is under the guard's minimum (an empty field is not). */
export function belowGuard(value: string, guard: PriceGuard | null): boolean {
  if (!guard || value.trim() === '') return false;
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 && n < guard.minimumPrice;
}
